import {
  APIConstraintEnforcementMode,
  APIPublicOrganizationAttributes,
  APIOrgTripleAttributes,
  APIReferenceType,
} from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';
import { partition } from 'lodash';
import { IMPLICIT_REFTYPE_ID } from '../models/model';

/**
 * Get reference types that adhere to the metamodel constraints for the given source and target types.
 */
export const getValidReferenceTypes = (
  referenceTypeNames: string[],
  sourceType: Maybe<string>,
  targetType: Maybe<string>,
  orgTriples: APIOrgTripleAttributes[]
): string[] => {
  const validTypeNames = orgTriples
    .filter(
      triple =>
        (sourceType ? triple.sourceType === sourceType : true) &&
        (targetType ? triple.targetType === targetType : true)
    )
    .map(triple => triple.referenceType);

  return referenceTypeNames.filter(
    value => value !== 'Implicit' && validTypeNames.includes(value)
  );
};

/**
 * Check if a reference type adheres to the metamodel constraints for the given list of valid reference type names
 */
export const isValidReferenceTypeName = (
  validReferenceTypeNames: string[],
  referenceTypeName: string
) =>
  referenceTypeName === 'Implicit' ||
  validReferenceTypeNames.includes(referenceTypeName);

/**
 * Check if a reference type adheres to the metamodel constraints for the given source and target types
 */
export const isIntendedRefType = (
  intendedRefTypes: IntendedRefTypes,
  sourceTypeName: string,
  targetTypeName: string,
  referenceTypeName: string
) => {
  const allowedRefTypeNames =
    intendedRefTypes[sourceTypeName]?.[targetTypeName];

  // If no intended ref types are defined for this source/target combination,
  // we consider it a violation (the triple is not explicitly allowed)
  return (
    !allowedRefTypeNames || !allowedRefTypeNames.includes(referenceTypeName)
  );
};

type SourceComponentTypeName = string;
type TargetComponentTypeName = string;
type ReferenceTypeName = string;

export type IntendedRefTypes = Record<
  SourceComponentTypeName,
  Record<TargetComponentTypeName, ReferenceTypeName[]>
>;

export const buildIntendedRefTypesMap = (
  triples: APIOrgTripleAttributes[] | null
): IntendedRefTypes | null => {
  if (!triples) {
    return null;
  }

  const intendedRefTypes: IntendedRefTypes = {};
  for (const triple of triples) {
    if (!intendedRefTypes[triple.sourceType]) {
      intendedRefTypes[triple.sourceType] = {};
    }
    if (!intendedRefTypes[triple.sourceType][triple.targetType]) {
      intendedRefTypes[triple.sourceType][triple.targetType] = [];
    }
    intendedRefTypes[triple.sourceType][triple.targetType].push(
      triple.referenceType
    );
  }
  return intendedRefTypes;
};

type ProcessedReferenceTypes = {
  availableRefTypes: APIReferenceType[];
  supportedRefTypes: APIReferenceType[];
  otherRefTypes: APIReferenceType[];
  hasGrouping: boolean;
};

export const processReferenceTypes = (
  availableRefTypes: APIReferenceType[],
  intendedRefTypes: IntendedRefTypes | null,
  isEnforcementEnabled: boolean,
  sourceType: string | null,
  targetType: string | null
): ProcessedReferenceTypes => {
  if (!sourceType || !targetType || !intendedRefTypes) {
    return {
      availableRefTypes,
      supportedRefTypes: availableRefTypes,
      otherRefTypes: [],
      hasGrouping: false,
    };
  }

  const intendedRefTypeNames = intendedRefTypes[sourceType]?.[targetType] || [];

  const [goodRefTypes, badRefTypes] = partition(availableRefTypes, refType =>
    intendedRefTypeNames.includes(refType.name)
  );

  const otherRefTypes = isEnforcementEnabled
    ? badRefTypes.filter(refType => refType.id === IMPLICIT_REFTYPE_ID)
    : badRefTypes;

  return {
    availableRefTypes,
    supportedRefTypes: goodRefTypes,
    otherRefTypes,
    hasGrouping: true,
  };
};

export const hasConstraints = (
  organizationApi: APIPublicOrganizationAttributes
) => {
  return (
    organizationApi.settings.constraintEnforcementMode === 'warning' ||
    organizationApi.settings.constraintEnforcementMode === 'strict'
  );
};

export const isRefTypeViolation = (
  intendedRefTypes: IntendedRefTypes | null,
  sourceTypeName: string | null,
  targetTypeName: string | null,
  selectedRefTypeName: string | null
): boolean => {
  if (!selectedRefTypeName || selectedRefTypeName === 'Implicit') return false;
  if (!intendedRefTypes || !sourceTypeName || !targetTypeName) return false;

  const allowedRefTypeNames =
    intendedRefTypes[sourceTypeName]?.[targetTypeName] ?? [];
  return !allowedRefTypeNames.includes(selectedRefTypeName);
};

export const isRefTypeBlocked = (
  intendedRefTypes: IntendedRefTypes | null,
  sourceTypeName: string | null,
  targetTypeName: string | null,
  selectedRefTypeName: string | null,
  enforcementMode: APIConstraintEnforcementMode
): boolean => {
  if (enforcementMode !== 'strict') return false;
  return isRefTypeViolation(
    intendedRefTypes,
    sourceTypeName,
    targetTypeName,
    selectedRefTypeName
  );
};

export const getBlockedRefTypeMessage = (
  selectedRefTypeName: string | null,
  sourceTypeName: string | null,
  targetTypeName: string | null
): string =>
  `"${selectedRefTypeName}" is not allowed between "${sourceTypeName}" and "${targetTypeName}".`;
